'use client'

import { useTenantButtonColor } from '@/components/TenantButtonColorContext'
import { useTenantDarkMode } from '@/components/TenantThemeModeContext'
import type { SurveySheetState } from '@/components/embedding/survey/useSurveySheet'
import { brandTextColorStyle, ctaButtonColorStyle } from '@/lib/colorUtils'
import { attachHlsSource } from '@/lib/hlsPlayer'
import { ScenarioNodeWithVideoUrl } from '@/orval/generated/public/model'
import dynamic from 'next/dynamic'
import { FC, RefObject, useEffect, useMemo, useRef } from 'react'
import { SPSurveyLayer, SPSurveySheetPanel } from '../ModalSurvey'
import VideoCarouselOverlay, { CarouselVideoItem } from '../video-carousel-overlay'
import VideoFeedbackScreen from '../video-feedback-screen/VideoFeedbackScreen'
import SPBottomSection from './SPBottomSection'
import SPVideoControls from './SPVideoControls'
import SPVideoInfoOverlay from './SPVideoInfoOverlay'
import { useSPCarousel } from './useSPCarousel'
import { useSPSwipeNavigation } from './useSPSwipeNavigation'
import { useSPVideoPlayback } from './useSPVideoPlayback'
import { DEFAULT_EMBED_FONT, embedFontStack, type EmbedFont } from '@/components/scenario/embedFont'

const MarkdownView = dynamic(
  () => import('@/components/ui/markdown-view/markdown-view').then((mod) => mod.MarkdownView),
  { ssr: false },
)

export interface SPVideoModalProps {
  nodes: ScenarioNodeWithVideoUrl[]
  currentNode: ScenarioNodeWithVideoUrl
  currentVideoIndex: number
  videoRef: RefObject<HTMLVideoElement | null>
  /**
   * マウント起点の自動再生を済ませた動画（nodeKey）。VideoModal が保持する。
   * PC/SP の切替で <video> が作り直されても、視聴者が止めた動画を再生し直さない。
   */
  autoPlayedNodeKeyRef?: { current: string | null }
  showEndScreen: boolean
  onClose: () => void
  onFullClose: () => void
  onPreviousVideo: () => void
  onNextVideo: () => void
  onVideoSelect: (index: number) => void
  onVideoEnd: () => void
  /** 動画評価を回答後に閉じる。同じ動画をもう一度再生する */
  onDismissEndScreen: () => void
  /** 動画評価をスキップして閉じる。再生し直さず、終了状態のまま止める */
  onSkipEndScreen: () => void
  /** 評価を閉じたあとに同じ動画を再生し直すか（回答後は true / スキップは false） */
  replayAfterEndScreen: boolean
  ctaUrl?: string | null
  ctaLabel?: string | null
  onTrackCtaClick?: (nodeKey: string, url: string, label?: string) => void
  scenarioId?: string
  isPreview?: boolean
  survey?: SurveySheetState
  /** 視聴モーダルのフォント（プレイリスト単位の設定） */
  embedFont?: EmbedFont
}

const SPVideoModal: FC<SPVideoModalProps> = ({
  nodes,
  currentNode,
  currentVideoIndex,
  videoRef,
  autoPlayedNodeKeyRef,
  showEndScreen,
  onFullClose,
  onPreviousVideo,
  onNextVideo,
  onVideoSelect,
  onVideoEnd,
  onDismissEndScreen,
  onSkipEndScreen,
  replayAfterEndScreen,
  ctaUrl,
  ctaLabel,
  onTrackCtaClick,
  scenarioId,
  isPreview = false,
  survey,
  embedFont = DEFAULT_EMBED_FONT,
}) => {
  const tenantButtonColor = useTenantButtonColor()
  const ctaStyle = ctaButtonColorStyle(tenantButtonColor)
  const titleStyle = brandTextColorStyle(tenantButtonColor, useTenantDarkMode())
  const videoContainerRef = useRef<HTMLDivElement>(null)

  // HLS対応: hlsUrl があれば HLS(native/hls.js)で再生、無ければ src(MP4)にフォールバック
  useEffect(() => {
    const video = videoRef.current
    if (!video || !currentNode.hlsUrl) return
    return attachHlsSource(video, {
      hlsUrl: currentNode.hlsUrl,
      mp4Url: currentNode.videoUrl,
      autoPlay: true,
    })
  }, [videoRef, currentNode.hlsUrl, currentNode.videoUrl])

  const hasPrevious = currentVideoIndex > 0
  const hasNext = currentVideoIndex < nodes.length - 1

  // Get video info (template takes priority, show each performer separately)
  const title = currentNode.video?.title || '動画タイトル'
  const performer1Name = currentNode.video?.performerTemplate?.name || currentNode.video?.performer || null
  const performer1Profile = currentNode.video?.performerTemplate?.profile || null
  const performer2Name = currentNode.video?.performerTemplate2?.name || null
  const performer1Department = currentNode.video?.performerTemplate?.department || null
  const performer2Profile = currentNode.video?.performerTemplate2?.profile || null
  const performer2Department = currentNode.video?.performerTemplate2?.department || null
  const videoText1 = currentNode.video?.videoTextTemplate?.videoText || currentNode.video?.videoText || ''
  const videoText2 = currentNode.video?.videoTextTemplate2?.videoText || ''

  // アンケート設問シート展開中は「詳しく見る」の詳細表示と同じく動画を上部に縮小し、
  // 残りをアンケートシートが占める（#244）。任意設問のオプトインバー待機中
  // （survey.showTab）は縮小せず再生継続。
  const surveyExpanded = Boolean(survey?.expanded && survey?.activeQuestion)

  const {
    state,
    setState,
    showControls,
    setShowControls,
    playbackState,
    shouldAutoPlayRef,
    handleVideoTap,
    handlePlay,
    handleVideoEnded,
    handlePlaybackRateChange,
    handleMuteToggle,
  } = useSPVideoPlayback({
    videoRef,
    currentVideoIndex,
    nodeKey: currentNode.nodeKey,
    autoPlayedNodeKeyRef,
    showEndScreen,
    replayAfterEndScreen,
    onVideoEnd,
  })

  const { showCarousel, handleShowCarousel, handleCloseCarousel, handleCarouselVideoSelect } = useSPCarousel({
    videoRef,
    shouldAutoPlayRef,
    onVideoSelect,
    setState,
  })

  // 「別の動画へ」カルーセルの候補（再生中を除く）。選択は id で返るので index に戻す。
  const carouselVideos = useMemo<CarouselVideoItem[]>(
    () =>
      nodes
        .filter((_, index) => index !== currentVideoIndex)
        .map((node) => ({
          id: node.id,
          thumbnailUrl: node.thumbnailUrl,
          animatedThumbnailUrl: node.animatedThumbnailUrl,
          title: node.video?.title,
        })),
    [nodes, currentVideoIndex],
  )
  const handleCarouselSelectById = (nodeId: string) => {
    const index = nodes.findIndex((node) => node.id === nodeId)
    if (index >= 0) handleCarouselVideoSelect(index)
  }

  const {
    animState,
    handleTouchStart,
    handleTouchEnd,
    handlePreviousWithAutoPlay,
    handleNextWithAutoPlay,
    handleShowDetail,
    handleCloseDetail,
  } = useSPSwipeNavigation({
    state,
    showCarousel,
    currentVideoIndex,
    hasPrevious,
    hasNext,
    videoRef,
    videoContainerRef,
    shouldAutoPlayRef,
    onVideoSelect,
    onPreviousVideo,
    onNextVideo,
    setState,
    setShowControls,
  })

  // CTA button handler
  const handleCTAClick = () => {
    if (ctaUrl) {
      onTrackCtaClick?.(currentNode.nodeKey, ctaUrl, ctaLabel ?? undefined)
      // 別タブへ移る間、裏で動画が進み続けないように止める。
      // 詳細パネルから押されたときはパネルを閉じない（state は 'detail' のまま）。
      const video = videoRef.current
      if (video && !video.paused) {
        video.pause()
        if (state !== 'detail') {
          setState('paused')
          setShowControls(true)
        }
      }
      window.open(ctaUrl, '_blank')
    }
  }

  return (
    <>
      <style>{`
          .sp-end-screen-container {
            position: fixed;
            inset: 0;
            z-index: 9999;
          }

        .sp-modal {
          position: fixed;
          inset: 0;
          /* background: #000; */
          background: #1a1a1a;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          /*
           * 親（shadowHost / container）が popup 表示モードで pointer-events: none を
           * 設定しているケース（parent-bridge 経由の forceOverlay 等）に備え、
           * モーダル本体は明示的にクリックを受け取れるよう auto を上書きする。
           * これが無いと閉じるボタンや動画コントロールがタップできない。
           */
          pointer-events: auto;
          /*
           * フォントを明示的に固定する（既定はゴシック＝Noto Sans JP、プレイリスト設定で変更可）。
           * Shadow DOM は CSS 変数（--default-font-family 等）を継承するため、
           * 親ページの font-family がモーダル内に漏れて見た目がサイト毎に変わる事象を防ぐ。
           * 固定をやめて inherit にするとこの事象が再発するので、値は必ず embedFontStack から採り、
           * 「先方サイトのフォントを継承」という選択肢は embedFont に持たせない。
           * Web フォント未到着の間は OS 既定でフォールバックし、到着後に swap される。
           */
          font-family: ${embedFontStack(embedFont)};
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
        }

        .sp-video-container {
          flex: 1;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 0;
          overflow: hidden;
          box-sizing: border-box;
        }

        .sp-video-container.detail-mode {
          flex: none;
          height: 24vh;
        }

        /* A6: Slide transition animations */
        .sp-video-container.slide-up {
          animation: spSlideOutUp 200ms ease-out forwards;
        }
        .sp-video-container.slide-down {
          animation: spSlideOutDown 200ms ease-out forwards;
        }
        .sp-video-container.slide-left {
          animation: spSlideOutLeft 200ms ease-out forwards;
        }

        @keyframes spSlideOutUp {
          from { transform: translateY(0); opacity: 1; }
          to { transform: translateY(-100%); opacity: 0; }
        }
        @keyframes spSlideOutDown {
          from { transform: translateY(0); opacity: 1; }
          to { transform: translateY(100%); opacity: 0; }
        }
        @keyframes spSlideOutLeft {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(-100%); opacity: 0; }
        }

        .sp-video {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .sp-video-overlay {
          position: absolute;
          inset: 0;
          cursor: pointer;
          z-index: 1;
        }

        .sp-header {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          padding: 12px 16px;
          padding-top: calc(12px + env(safe-area-inset-top, 0px));
          background: linear-gradient(to bottom, rgba(0,0,0,0.7), transparent);
          z-index: 10;
        }

        .sp-header-label {
          font-size: 10px;
          color: rgba(255,255,255,0.7);
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 4px;
        }

        .sp-header-title {
          font-size: 14px;
          font-weight: bold;
          color: white;
        }

        .sp-video-title {
          font-size: 16px;
          font-weight: bold;
          color: white;
          margin-bottom: 6px;
          line-height: 1.4;
        }

        .sp-video-subtitle {
          font-size: 13px;
          color: rgba(255,255,255,0.8);
          margin-bottom: 12px;
          line-height: 1.5;
        }

        .sp-detail-button {
          display: inline-block;
          padding: 8px 16px;
          background: transparent;
          color: var(--video-brand-color, var(--video-accent-color));
          font-size: 13px;
          font-weight: bold;
          border: 1px solid var(--video-brand-color, var(--video-accent-color));
          border-radius: 20px;
          cursor: pointer;
          margin-bottom: 12px;
        }

        @keyframes detailSlideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }

        @keyframes detailSlideDown {
          from { transform: translateY(0); }
          to { transform: translateY(100%); }
        }

        /* Detail panel (TikTok-style inline) */
        .sp-detail-panel {
          flex: 1;
          min-height: 0;
          background: white;
          border-radius: 20px 20px 0 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          animation: detailSlideUp 300ms ease-out;
        }

        .sp-detail-panel.closing {
          animation: detailSlideDown 300ms ease-out forwards;
        }

        .sp-detail-header {
          /* padding: 10px 10px 0px; */
          display: flex;
          justify-content: flex-end;
          flex-shrink: 0;
          position: relative;
        }

        .sp-detail-close {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 30px;
          height: 30px;
          background: rgba(255, 255, 255, 0.7);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          cursor: pointer;
          color: #666;
        }

        .sp-detail-close:active {
          background: rgba(0, 0, 0, 0.1);
        }

        .sp-detail-content {
          flex: 1;
          overflow-y: auto;
          padding: 36px 20px 0px;
          text-align: justify;
        }

        .sp-detail-title {
          font-size: 21px;
          font-weight: bold;
          color: var(--video-brand-color, var(--video-accent-color, #1d48af));
          line-height: 1.5;
          margin-bottom: 14px;
        }

        .sp-detail-section {
          margin-bottom: 24px;
          /* padding-left: 12px; */
          /* border-left: 3px solid #2196F3; */
        }

        .sp-detail-section .markdown-view {
          font-size: 16px;
        }

        /* 詳細パネルは常に白背景のため、テナントのダークテーマで設定される
           MarkdownView の明るい文字色を打ち消して読みやすさを保つ。 */
        .dark .sp-detail-panel .markdown-view {
          color: #444;
        }

        .dark .sp-detail-panel .markdown-view h1,
        .dark .sp-detail-panel .markdown-view h2,
        .dark .sp-detail-panel .markdown-view h3,
        .dark .sp-detail-panel .markdown-view h4,
        .dark .sp-detail-panel .markdown-view h5,
        .dark .sp-detail-panel .markdown-view h6,
        .dark .sp-detail-panel .markdown-view strong {
          color: #333;
        }

      .sp-detail-section-label {
        font-size: 12px;
        font-weight: bold;
        color: #808080;
        margin-bottom: 6px;
        /* padding-left: 0; */
        /* border-left: none; */
      }


        .sp-detail-section-value {
          font-size: 18px;
          font-weight:bold;
          /* margin-bottom:8px; */
        }

      .sp-detail-performer-members-group {
        background: #f7f8fa;
        border: 1px solid rgba(0, 0, 0, .07);
        border-radius: 12px;
        overflow: hidden;
      }

      .sp-detail-performer-members {
        display: flex;
        flex-direction: column;
        gap: 0;
        padding: 12px;
      }

      .sp-detail-performer-members + .sp-detail-performer-members {
        padding-top: 0;
      }

      .sp-detail-performer-header {
        display: flex;
        align-items: center;
        gap: 14px;
      }

        .sp-detail-performer-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, color-mix(in srgb, var(--video-brand-color, var(--video-accent-color)) 8%, transparent) 0%, color-mix(in srgb, var(--video-brand-color, var(--video-accent-color)) 18%, transparent) 100%);
          border: 1.5px solid color-mix(in srgb, var(--video-brand-color, var(--video-accent-color)) 24%, transparent);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: var(--video-brand-color, var(--video-accent-color));
        }

        .sp-detail-performer-info {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

      .sp-detail-performer-department {
        font-size: 13px;
        margin-top: 3px;
      }

      .sp-detail-performer-profile {
        width: 100%;
      }

      .sp-detail-performer-profile .markdown-view {
        margin-top: 10px;
        color: #5a5a5a;
        font-size: 14.5px;
        background: #ffffff;
        padding:10px 14px;
        border-radius: 10px;
      }


        .sp-detail-heading {
          font-size: 16px;
          font-weight: bold;
          color: #333;
          margin-bottom: 12px;
          padding-left: 12px;
          border-left: 3px solid var(--video-brand-color, var(--video-accent-color));
        }

        .sp-detail-bottom-fade {
          position: relative;
        }

        .sp-detail-bottom-fade::before {
          content: '';
          position: absolute;
          bottom: 100%;
          left: 0;
          right: 0;
          height: 40px;
          background: linear-gradient(transparent, white);
          pointer-events: none;
        }

        .sp-detail-cta-container {
          flex-shrink: 0;
          padding: 8px 12px;
          padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px));
        }

        .sp-detail-cta-button {
          width: 100%;
          padding: 12px 24px;
          background: var(--video-brand-color, var(--video-accent-color, #1d48af));
          color: white;
          font-size: 16px;
          font-weight: bold;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          box-shadow: 0 4px 12px color-mix(in srgb, var(--video-brand-color, var(--video-accent-color)) 30%, transparent);
        }

        .sp-detail-cta-button:hover {
          background: var(--video-brand-hover-color, var(--video-accent-hover-color, #17398c));
        }

        /* A5: Back indicator */
        .sp-back-indicator {
          position: absolute;
          top: 50%;
          left: 8px;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.4);
          border-radius: 50%;
          color: white;
          z-index: 15;
          opacity: 0.7;
          pointer-events: none;
        }
      `}</style>

      <div className="sp-modal">
        {/* A3: Backdrop tap area for PiP transition */}
        {/* Video Area */}
        <div
          className={`sp-video-container ${state === 'detail' || surveyExpanded ? 'detail-mode' : ''} ${animState.slideAnimation ? `slide-${animState.slideAnimation}` : ''}`}
          ref={videoContainerRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <video
            ref={videoRef}
            className="sp-video"
            // hlsUrl 指定時は effect が src を動的設定する(MP4の二重ロード回避)
            src={currentNode.hlsUrl ? undefined : currentNode.videoUrl}
            poster={currentNode.thumbnailUrl || undefined}
            playsInline
            onEnded={handleVideoEnded}
          />

          {/* Tap overlay */}
          <div className="sp-video-overlay" onClick={handleVideoTap} />

          <SPVideoControls
            playbackRate={playbackState.playbackRate}
            isMuted={playbackState.isMuted}
            showControls={showControls}
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            isDetailMode={state === 'detail'}
            onFullClose={onFullClose}
            onPlaybackRateChange={handlePlaybackRateChange}
            onMuteToggle={handleMuteToggle}
            onPrevious={handlePreviousWithAutoPlay}
            onPlay={handlePlay}
            onNext={handleNextWithAutoPlay}
          />

          {/* Video info overlay - visible when not in detail mode and carousel is not shown */}
          {state !== 'detail' && !showCarousel && !surveyExpanded && !showEndScreen && (
            <SPVideoInfoOverlay title={title} onShowDetail={handleShowDetail} />
          )}

          {/* 動画評価（参考になった／参考にならなかった）。動画エリアだけを覆い、
              下の「別の動画へ｜CTA」とモーダルの閉じるボタンは押せるまま残す。 */}
          {showEndScreen && !showCarousel && (
            <VideoFeedbackScreen
              videoId={currentNode.videoId}
              scenarioId={scenarioId}
              variant="sp"
              onDone={onDismissEndScreen}
              onSkip={onSkipEndScreen}
              trackingEnabled={!isPreview}
            />
          )}
        </div>

        {/* アンケート（#244）: 任意設問の待機中は、動画の下・CTAボタン行の上に行として置く。
            動画に重ねると動画タイトルや「詳しく見る」に被るため（動画個別ページの SP と同じ階層）。 */}
        {survey && state !== 'detail' && !showCarousel && <SPSurveyLayer survey={survey} />}

        {/* アンケート設問シート（#244）: 展開中は動画を縮小し、その下に別枠で表示 */}
        {survey && <SPSurveySheetPanel survey={survey} />}

        {/* Detail Panel (TikTok-style: bottom 2/3) - アンケート展開中はシートを優先し出さない */}
        {state === 'detail' && !surveyExpanded && (
          <div className={`sp-detail-panel ${animState.isDetailClosing ? 'closing' : ''}`}>
            <div className="sp-detail-header">
              <button type="button" className="sp-detail-close" onClick={handleCloseDetail}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="sp-detail-content">
              <h2 className="sp-detail-title" style={titleStyle}>
                {title}
              </h2>

              {(performer1Name || performer2Name) && (
                <div className="sp-detail-section">
                  {/* ラベルを置かない理由は VideoInfoContent と同じ（PC と表示を揃える）。 */}
                  <div className="sp-detail-performer-members-group">
                    {performer1Name && (
                      <div className="sp-detail-performer-members">
                        <div className="sp-detail-performer-header">
                          <div className="sp-detail-performer-avatar">
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                            >
                              <circle cx="12" cy="8" r="4" />
                              <path d="M20 21a8 8 0 1 0-16 0" />
                            </svg>
                          </div>
                          <div className="sp-detail-performer-info">
                            <div className="sp-detail-section-value">{performer1Name}</div>
                            {performer1Department && (
                              <div className="sp-detail-performer-department">{performer1Department}</div>
                            )}
                          </div>
                        </div>
                        {performer1Profile && (
                          <div className="sp-detail-performer-profile">
                            <MarkdownView content={performer1Profile} />
                          </div>
                        )}
                      </div>
                    )}
                    {performer2Name && (
                      <div className="sp-detail-performer-members">
                        <div className="sp-detail-performer-header">
                          <div className="sp-detail-performer-avatar">
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                            >
                              <circle cx="12" cy="8" r="4" />
                              <path d="M20 21a8 8 0 1 0-16 0" />
                            </svg>
                          </div>
                          <div className="sp-detail-performer-info">
                            <div className="sp-detail-section-value">{performer2Name}</div>
                            {performer2Department && (
                              <div className="sp-detail-performer-department">{performer2Department}</div>
                            )}
                          </div>
                        </div>
                        {performer2Profile && (
                          <div className="sp-detail-performer-profile">
                            <MarkdownView content={performer2Profile} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {videoText1 && (
                <div className="sp-detail-section">
                  {/* <div className="sp-detail-section-label">動画テキスト</div> */}
                  <MarkdownView content={videoText1} />
                </div>
              )}

              {videoText2 && (
                <div className="sp-detail-section">
                  {/* <div className="sp-detail-section-label">動画テキスト</div> */}
                  <MarkdownView content={videoText2} />
                </div>
              )}
            </div>

            {ctaUrl && (
              <div className="sp-detail-bottom-fade">
                <div className="sp-detail-cta-container">
                  <button type="button" className="sp-detail-cta-button" style={ctaStyle} onClick={handleCTAClick}>
                    {ctaLabel || '採用情報へ'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bottom Section - visible when not in detail mode, not answering survey, and carousel is not shown */}
        {state !== 'detail' && !showCarousel && !surveyExpanded && (
          <SPBottomSection
            ctaUrl={ctaUrl}
            ctaLabel={ctaLabel}
            onShowCarousel={handleShowCarousel}
            onCTAClick={handleCTAClick}
          />
        )}

        {/* Video Carousel Overlay */}
        {showCarousel && (
          <VideoCarouselOverlay
            videos={carouselVideos}
            onVideoSelect={handleCarouselSelectById}
            onClose={handleCloseCarousel}
          />
        )}
      </div>
    </>
  )
}

export default SPVideoModal
